import * as Dialog from '@radix-ui/react-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import type { ReactNode } from 'react';
import { Drawer } from 'vaul';

import { useMediaQuery } from '@/shared/hooks/use-media-query';
import { cn } from '@/shared/lib/cn';

export interface SheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  testID?: string;
  className?: string;
  /**
   * Quando true, a bandeja MOBILE (vaul Drawer) abre com altura fixa de 90dvh
   * em vez de `h-auto` (altura dirigida pelo conteudo). Util para sheets de
   * formulario longo (ex: gerenciar categorias) que ficavam pequenos demais
   * quando o conteudo era curto. NAO afeta o desktop (Dialog centralizado
   * segue dimensionado pelo conteudo). Os filhos devem usar `flex-1 min-h-0
   * overflow-y-auto` para rolar dentro da altura fixa.
   */
  mobileFullHeight?: boolean;
}

/**
 * Sheet unificado:
 *  - Mobile (<900px): vaul Drawer (slide-up).
 *  - Desktop (>=900px): Radix Dialog centralizado (modal).
 *
 * `title` e obrigatorio para a11y. Se nao deve ser visivel, use `description`
 * vazio e estilize como `sr-only` no children — ou melhor, use o `<Sheet.Title>`
 * exposto pelo wrapper proprio. Aqui mantemos `title` como string para reduzir API.
 */
export function Sheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  testID,
  className,
  mobileFullHeight = false,
}: SheetProps) {
  const isDesktop = useMediaQuery('(min-width: 900px)');

  if (isDesktop) {
    return (
      <Dialog.Root open={open} onOpenChange={onOpenChange}>
        <Dialog.Portal>
          <Dialog.Overlay
            className="fixed inset-0 z-[var(--z-backdrop)] animate-fade-in bg-black/60"
            data-testid={testID ? `${testID}-overlay` : undefined}
          />
          <Dialog.Content
            data-testid={testID}
            className={cn(
              'fixed left-1/2 top-1/2 z-[var(--z-modal)] w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-card bg-surface-highest p-6 shadow-modal',
              className,
            )}
          >
            <Dialog.Title className="mb-2 text-display-sm text-on-surface">{title}</Dialog.Title>
            {description ? (
              <Dialog.Description className="mb-4 text-body-md text-secondary-text">
                {description}
              </Dialog.Description>
            ) : (
              <VisuallyHidden.Root>
                <Dialog.Description>{title}</Dialog.Description>
              </VisuallyHidden.Root>
            )}
            {children}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    );
  }

  // Guards de dismiss centralizados — toda bandeja (sheet mobile) so fecha
  // por gesto deliberado, nunca por um arrasto/flick acidental no conteudo:
  //  - `handleOnly`: apenas o `<Drawer.Handle/>` arrasta para fechar; drag
  //    no corpo da bandeja e ignorado pelo vaul.
  //  - `closeThreshold` 0.4: exige arrastar ~40% da altura antes de fechar
  //    (default do vaul e baixo, fecha com pouco movimento).
  //  - `dismissible` true: backdrop click + Esc continuam fechando (a11y).
  return (
    <Drawer.Root
      open={open}
      onOpenChange={onOpenChange}
      handleOnly
      closeThreshold={0.4}
      dismissible
    >
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 z-[var(--z-backdrop)] bg-black/60" />
        <Drawer.Content
          data-testid={testID}
          className={cn(
            // max-h em dvh (nao vh) para a bandeja acompanhar o viewport
            // dinamico quando o teclado virtual abre/fecha (sem vao em branco).
            'fixed inset-x-0 bottom-0 z-[var(--z-modal)] mt-24 flex max-h-[90dvh] flex-col rounded-t-card bg-surface-highest p-4',
            // `mobileFullHeight`: altura fixa 90dvh (form longo) vs `h-auto`
            // (dirigida pelo conteudo). So no mobile; desktop usa Dialog acima.
            mobileFullHeight ? 'h-[90dvh]' : 'h-auto',
            className,
          )}
        >
          <Drawer.Handle className="mx-auto mb-3 h-1.5 w-12 flex-shrink-0 rounded-pill bg-surface-high" />
          <Drawer.Title className="mb-2 text-display-sm text-on-surface">{title}</Drawer.Title>
          {description ? (
            <Drawer.Description className="mb-4 text-body-md text-secondary-text">
              {description}
            </Drawer.Description>
          ) : (
            <VisuallyHidden.Root>
              <Drawer.Description>{title}</Drawer.Description>
            </VisuallyHidden.Root>
          )}
          {children}
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
